// client/src/components/catalogoModelo/GerarCatalogoIAForm.js
import React, { useState } from 'react';
import { Sparkles, Loader2, Trash2, CheckCircle2 } from 'lucide-react';
import { useSegmentos } from '../../hooks/useSegmentos';
import { useGerarCatalogoIA, useCriarEmMassaCatalogoModelo } from '../../hooks/useCatalogoModelo';
import { useGeracaoIAGate, GeracaoIAUpsellBanner } from '../ai/useGeracaoIAGate';
import ResultadoImportacaoTable from './ResultadoImportacaoTable';

const GerarCatalogoIAForm = ({ onClose }) => {
    const { data: segmentos, isLoading: isLoadingSegmentos } = useSegmentos();
    const gerarCatalogo = useGerarCatalogoIA();
    const criarEmMassa = useCriarEmMassaCatalogoModelo();
    const { podeGerarComIA, planPermiteIA } = useGeracaoIAGate();

    const [segmentoId, setSegmentoId] = useState('');
    const [categoriasTexto, setCategoriasTexto] = useState('');
    const [quantidadePorCategoria, setQuantidadePorCategoria] = useState(5);
    const [contexto, setContexto] = useState('');

    const [propostaSegmento, setPropostaSegmento] = useState(null);
    const [itensPropostos, setItensPropostos] = useState(null); // null = ainda não gerou
    const [resultado, setResultado] = useState(null);

    if (!podeGerarComIA) {
        return (
            <div>
                <GeracaoIAUpsellBanner planPermiteIA={planPermiteIA} />
                <div className="form-actions">
                    <button type="button" onClick={onClose} className="action-button action-button-secondary">Fechar</button>
                </div>
            </div>
        );
    }

    const categorias = categoriasTexto.split(',').map((c) => c.trim()).filter(Boolean);

    const handleGerar = (e) => {
        e.preventDefault();
        if (categorias.length === 0 || !segmentoId) return;

        gerarCatalogo.mutate(
            { segmento: segmentoId, categorias, quantidadePorCategoria: Number(quantidadePorCategoria), contexto: contexto || undefined },
            {
                onSuccess: (data) => {
                    setPropostaSegmento(data.segmento);
                    setItensPropostos(data.itens);
                }
            }
        );
    };

    const atualizarItem = (index, campo, valor) => {
        setItensPropostos((prev) => prev.map((item, i) => (i === index ? { ...item, [campo]: valor } : item)));
    };

    const removerItem = (index) => {
        setItensPropostos((prev) => prev.filter((_, i) => i !== index));
    };

    const handleCriarTodos = () => {
        if (!itensPropostos || itensPropostos.length === 0) return;
        criarEmMassa.mutate(
            {
                segmento: propostaSegmento._id,
                itens: itensPropostos.map((item) => ({
                    ...item,
                    precoSugerido: item.precoSugerido !== '' ? Number(item.precoSugerido) : undefined,
                    duracaoSugeridaMinutos: item.duracaoSugeridaMinutos !== '' ? Number(item.duracaoSugeridaMinutos) : undefined
                }))
            },
            { onSuccess: (data) => setResultado(data.data) }
        );
    };

    const handleGerarNovamente = () => {
        setItensPropostos(null);
        setPropostaSegmento(null);
        setResultado(null);
    };

    if (resultado) {
        return (
            <div>
                <div className="form-section">
                    <h4 className="form-section-title">
                        Resultado — {resultado.totalCriados} criados, {resultado.totalDuplicados} duplicados, {resultado.totalErros} com erro
                        {' '}(de {resultado.totalItens} itens)
                    </h4>
                    <ResultadoImportacaoTable resultados={resultado.resultados} />
                </div>
                <div className="form-actions">
                    <button type="button" onClick={handleGerarNovamente} className="action-button action-button-secondary">
                        Gerar Outro Catálogo
                    </button>
                    <button type="button" onClick={onClose} className="action-button action-button-primary">
                        <CheckCircle2 size={18} className="icon" /> Concluir
                    </button>
                </div>
            </div>
        );
    }

    if (itensPropostos) {
        return (
            <div>
                <div className="form-section">
                    <h4 className="form-section-title">
                        Prévia do Catálogo — {itensPropostos.length} item{itensPropostos.length !== 1 ? 's' : ''} ({propostaSegmento?.nome})
                    </h4>
                    <p className="form-hint" style={{ marginBottom: 16 }}>
                        Revise e edite os itens antes de criar. Nada foi salvo ainda.
                    </p>
                    <div className="table-wrapper">
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Categoria</th>
                                    <th style={{ textAlign: 'left' }}>Nome</th>
                                    <th style={{ textAlign: 'left' }}>Descrição</th>
                                    <th>Preço (R$)</th>
                                    <th>Duração (min)</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {itensPropostos.map((item, index) => (
                                    <tr key={`${item.categoriaNome}-${index}`}>
                                        <td>{item.categoriaNome}</td>
                                        <td>
                                            <input
                                                className="form-input"
                                                value={item.nome}
                                                onChange={(e) => atualizarItem(index, 'nome', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className="form-input"
                                                value={item.descricao || ''}
                                                onChange={(e) => atualizarItem(index, 'descricao', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number" step="0.01" min="0"
                                                className="form-input" style={{ width: 100 }}
                                                value={item.precoSugerido ?? ''}
                                                onChange={(e) => atualizarItem(index, 'precoSugerido', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number" min="1"
                                                className="form-input" style={{ width: 90 }}
                                                value={item.duracaoSugeridaMinutos ?? ''}
                                                onChange={(e) => atualizarItem(index, 'duracaoSugeridaMinutos', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <button type="button" onClick={() => removerItem(index)} className="action-button-icon text-red-600" title="Remover">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="form-actions">
                    <button type="button" onClick={handleGerarNovamente} className="action-button action-button-secondary">
                        Voltar
                    </button>
                    <button
                        type="button"
                        onClick={handleCriarTodos}
                        disabled={itensPropostos.length === 0 || criarEmMassa.isPending}
                        className="action-button action-button-primary"
                    >
                        {criarEmMassa.isPending ? <Loader2 size={18} className="icon animate-spin" /> : <CheckCircle2 size={18} className="icon" />}
                        Criar {itensPropostos.length} Item{itensPropostos.length !== 1 ? 's' : ''}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleGerar}>
            <div className="form-section">
                <h4 className="form-section-title">Gerar Catálogo de Serviços com IA</h4>
                <p className="form-hint" style={{ marginBottom: 16 }}>
                    Informe o segmento e as categorias — a IA propõe um catálogo completo de serviços, com nome,
                    descrição, preço e duração sugeridos. Nada é salvo até você revisar e confirmar.
                </p>
                <div className="form-grid-container-cols-2">
                    <div className="form-group">
                        <label className="form-label required">Segmento</label>
                        <select value={segmentoId} onChange={(e) => setSegmentoId(e.target.value)} className="form-input" required disabled={isLoadingSegmentos}>
                            <option value="">Selecione...</option>
                            {segmentos?.map((segmento) => (
                                <option key={segmento._id} value={segmento._id}>{segmento.nome}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Itens por Categoria</label>
                        <input
                            type="number" min="1" max="10"
                            value={quantidadePorCategoria}
                            onChange={(e) => setQuantidadePorCategoria(e.target.value)}
                            className="form-input"
                        />
                    </div>
                    <div className="form-group form-group--full-width">
                        <label className="form-label required">Categorias (separadas por vírgula)</label>
                        <input
                            value={categoriasTexto}
                            onChange={(e) => setCategoriasTexto(e.target.value)}
                            className="form-input"
                            placeholder="Ex.: Cortes, Depilação, Estética Facial, Massagem"
                            required
                        />
                    </div>
                    <div className="form-group form-group--full-width">
                        <label className="form-label">Contexto adicional (opcional)</label>
                        <textarea
                            value={contexto}
                            onChange={(e) => setContexto(e.target.value)}
                            className="form-input"
                            rows={2}
                            placeholder="Ex.: público-alvo classe A/B, salão de alto padrão, foco em bem-estar"
                        />
                    </div>
                </div>
            </div>
            <div className="form-actions">
                <button type="button" onClick={onClose} className="action-button action-button-secondary">
                    Fechar
                </button>
                <button
                    type="submit"
                    disabled={categorias.length === 0 || !segmentoId || gerarCatalogo.isPending}
                    className="action-button action-button-primary"
                >
                    {gerarCatalogo.isPending ? <Loader2 size={18} className="icon animate-spin" /> : <Sparkles size={18} className="icon" />}
                    Gerar Catálogo
                </button>
            </div>
        </form>
    );
};

export default GerarCatalogoIAForm;
